<template>
  <CRow>
    <CCol col="12">
      <CCard>
        <CCardHeader>
          <CIcon name="cil-building" />
          <h5 class="d-inline ml-2">Billing entities</h5>
          <div class="card-header-actions">
            <CButton color="primary" size="sm" @click="newEntity">Add entity</CButton>
          </div>
        </CCardHeader>
        <CCardBody>
          <CAlert color="danger" v-if="error">{{ error }}</CAlert>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead><tr><th>Code</th><th>Legal name</th><th>Country</th><th>Period</th><th>Status</th><th>Readiness</th><th></th></tr></thead>
              <tbody>
                <tr v-for="entity in entities" :key="entity.id">
                  <td><strong>{{ entity.code }}</strong></td>
                  <td>{{ entity.legal_name }}</td>
                  <td>{{ entity.country_code }}</td>
                  <td>{{ formatDate(entity.active_from) }} – {{ formatDate(entity.active_until) || 'open' }}</td>
                  <td><CBadge :color="statusColor(entity.status)">{{ entity.status }}</CBadge></td>
                  <td>
                    <CBadge :color="entity.ready ? 'success' : 'warning'">
                      {{ entity.ready ? 'Ready' : `${entity.readiness_errors.length} checks remaining` }}
                    </CBadge>
                  </td>
                  <td><CButton size="sm" color="info" variant="outline" @click="editEntity(entity)">Manage</CButton></td>
                </tr>
              </tbody>
            </table>
          </div>
        </CCardBody>
      </CCard>
    </CCol>

    <CCol lg="7" v-if="selected">
      <CCard>
        <CCardHeader><strong>{{ selected.id ? `Entity ${selected.code}` : 'New billing entity' }}</strong></CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="3"><CInput label="Code" v-model.trim="form.code" placeholder="BE02" :disabled="codeLocked" /></CCol>
            <CCol md="3"><CInput label="Country" v-model.trim="form.country_code" placeholder="BE" /></CCol>
            <CCol md="6"><CInput label="Legal name" v-model.trim="form.legal_name" /></CCol>
            <CCol md="6"><CInput label="Trade name" v-model.trim="form.trade_name" /></CCol>
            <CCol md="3"><CInput label="Enterprise number" v-model.trim="form.enterprise_number" /></CCol>
            <CCol md="3"><CInput label="VAT number" v-model.trim="form.vat_number" /></CCol>
            <CCol md="8"><CInput label="Street" v-model.trim="form.address_street" /></CCol>
            <CCol md="4"><CInput label="Street 2" v-model.trim="form.address_street_2" /></CCol>
            <CCol md="3"><CInput label="Postal code" v-model.trim="form.address_postal_code" /></CCol>
            <CCol md="5"><CInput label="City" v-model.trim="form.address_city" /></CCol>
            <CCol md="4"><CInput label="Contact name" v-model.trim="form.contact_name" /></CCol>
            <CCol md="6"><CInput label="Contact email" type="email" v-model.trim="form.contact_email" /></CCol>
            <CCol md="6"><CInput label="Contact phone" v-model.trim="form.contact_phone" /></CCol>
            <CCol md="5"><CInput label="IBAN" v-model.trim="form.iban" /></CCol>
            <CCol md="3"><CInput label="BIC" v-model.trim="form.bic" /></CCol>
            <CCol md="4"><CInput label="Bank" v-model.trim="form.bank_name" /></CCol>
            <CCol md="4"><CInput label="Peppol participant" v-model.trim="form.peppol_participant_id" /></CCol>
            <CCol md="2"><CInput label="Scheme" v-model.trim="form.peppol_scheme_id" /></CCol>
            <CCol md="3"><CInput label="Peppol mailbox" type="email" v-model.trim="form.peppol_mailbox" /></CCol>
            <CCol md="3"><CInput label="Invoice sender" type="email" v-model.trim="form.email_from" /></CCol>
            <CCol md="3"><CInput label="Accountant email(s)" v-model.trim="form.accountant_email" placeholder="accountant@example.com" /></CCol>
            <CCol md="3"><CInput label="Voucher report email(s)" v-model.trim="form.voucher_report_email" placeholder="Optional" /></CCol>
            <CCol md="4"><CInput label="Provider profile reference" v-model.trim="form.payment_provider_profile" placeholder="BE02" /></CCol>
            <CCol md="4"><CInput label="Sales number format" v-model.trim="form.sales_number_format" /></CCol>
            <CCol md="4"><CInput label="Purchase number format" v-model.trim="form.purchase_number_format" /></CCol>
          </CRow>
        </CCardBody>
        <CCardFooter>
          <CButton color="primary" :disabled="saving" @click="saveEntity">Save</CButton>
          <CButton color="link" @click="selected = null">Close</CButton>
        </CCardFooter>
      </CCard>
    </CCol>

    <CCol lg="5" v-if="selected && selected.id">
      <CCard>
        <CCardHeader><strong>Readiness</strong></CCardHeader>
        <CCardBody>
          <CAlert :color="selected.ready ? 'success' : 'warning'">
            {{ selected.ready ? 'All required data and tests are complete.' : 'Activation remains blocked.' }}
          </CAlert>
          <ul v-if="selected.readiness_errors.length" class="pl-3">
            <li v-for="item in selected.readiness_errors" :key="item">{{ readinessLabel(item) }}</li>
          </ul>
          <div class="mt-3">
            <CButton v-for="test in tests" :key="test" size="sm" class="mr-2 mb-2" color="success" variant="outline" @click="recordTest(test)">
              Confirm {{ test }} test
            </CButton>
          </div>
          <CButton class="mt-2" size="sm" color="info" variant="outline" @click="loadBalances(selected.id)">
            View restaurant balances
          </CButton>
        </CCardBody>
      </CCard>
    </CCol>

    <CCol col="12" v-if="balanceEntityId">
      <CCard>
        <CCardHeader><strong>Restaurant balances for {{ balanceEntityCode }}</strong></CCardHeader>
        <CCardBody>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead><tr><th>Restaurant</th><th class="text-right">Balance</th><th>Updated</th></tr></thead>
              <tbody>
                <tr v-for="account in balances" :key="account.id">
                  <td>#{{ account.restid }} {{ account.restaurant ? account.restaurant.restaurant_name : '' }}</td>
                  <td class="text-right">{{ Number(account.balance || 0).toFixed(2) }}</td>
                  <td>{{ formatDate(account.updated_at) }}</td>
                </tr>
                <tr v-if="balances.length === 0"><td colspan="3">No balances for this entity.</td></tr>
              </tbody>
            </table>
          </div>
        </CCardBody>
      </CCard>
    </CCol>

    <CCol lg="6">
      <CCard>
        <CCardHeader><strong>Schedule transition</strong></CCardHeader>
        <CCardBody>
          <CAlert color="info">Only a future Sunday at 00:00 Europe/Brussels is accepted. There is no fallback entity.</CAlert>
          <CSelect label="From" :options="entityOptions" :value.sync="transition.from_billing_entity_id" />
          <CSelect label="To" :options="entityOptions" :value.sync="transition.to_billing_entity_id" />
          <CInput label="Transition time" type="datetime-local" v-model="transition.transition_at" />
          <CButton color="danger" :disabled="saving" @click="scheduleTransition">Schedule transition</CButton>
        </CCardBody>
      </CCard>
    </CCol>

    <CCol lg="6">
      <CCard>
        <CCardHeader><strong>Transition history</strong></CCardHeader>
        <CCardBody>
          <div v-for="item in transitions" :key="item.id" class="border-bottom py-2">
            <strong>{{ item.from_entity.code }} → {{ item.to_entity.code }}</strong>
            <span class="ml-2">{{ formatDate(item.transition_at) }}</span>
            <CBadge class="ml-2" :color="statusColor(item.status)">{{ item.status }}</CBadge>
            <CButton v-if="item.status === 'scheduled'" class="float-right" size="sm" color="danger" variant="outline" @click="cancelTransition(item)">Cancel</CButton>
          </div>
        </CCardBody>
      </CCard>
    </CCol>

    <CCol col="12">
      <CCard>
        <CCardHeader><strong>Billing entity audit log</strong></CCardHeader>
        <CCardBody>
          <div class="table-responsive">
            <table class="table table-sm">
              <thead><tr><th>Time</th><th>Entity</th><th>Action</th><th>Actor</th></tr></thead>
              <tbody>
                <tr v-for="entry in auditLogs" :key="entry.id">
                  <td>{{ formatDate(entry.created_at) }}</td>
                  <td>{{ entityCode(entry.billing_entity_id) }}</td>
                  <td>{{ entry.action }}</td>
                  <td>{{ entry.actor_id || 'system' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CCardBody>
      </CCard>
    </CCol>
  </CRow>
</template>

<script>
const emptyForm = () => ({
  code: 'BE02', country_code: 'BE', legal_name: '', trade_name: '', enterprise_number: '', vat_number: '',
  peppol_participant_id: '', peppol_scheme_id: '', address_street: '', address_street_2: '',
  address_postal_code: '', address_city: '', contact_name: '', contact_email: '', contact_phone: '',
  iban: '', bic: '', bank_name: '', email_from: '', accountant_email: '', voucher_report_email: '', peppol_mailbox: '', payment_provider_profile: 'BE02',
  sales_number_format: '{ENTITY}-{YEAR}-{SEQ}', purchase_number_format: '{ENTITY}-PI-{YEAR}-{SEQ}', sequence_padding: 6,
});

export default {
  name: 'BillingEntities',
  data() {
    return {
      entities: [], transitions: [], auditLogs: [], balances: [], balanceEntityId: null,
      selected: null, form: emptyForm(), saving: false, error: '',
      tests: ['payment', 'webhook', 'refund', 'pdf', 'ubl', 'sepa'],
      transition: { from_billing_entity_id: null, to_billing_entity_id: null, transition_at: '' },
    };
  },
  computed: {
    codeLocked() { return Boolean(this.selected && this.selected.sequences_count > 0); },
    balanceEntityCode() { return this.entityCode(this.balanceEntityId); },
    entityOptions() {
      return [{ value: null, label: 'Select entity' }].concat(this.entities.map(item => ({ value: item.id, label: `${item.code} — ${item.legal_name}` })));
    },
  },
  mounted() { this.load(); },
  methods: {
    endpoint(template, values = {}) { return Object.keys(values).reduce((url, key) => url.replace(`{${key}}`, values[key]), template); },
    async load() {
      this.error = '';
      try {
        const [entities, transitions, audit] = await Promise.all([
          this.$axios.get(this.$backend.BILLING_ENTITIES.GET_ALL),
          this.$axios.get(this.$backend.BILLING_ENTITIES.TRANSITIONS),
          this.$axios.get(this.$backend.BILLING_ENTITIES.AUDIT),
        ]);
        this.entities = entities.data;
        this.transitions = transitions.data;
        this.auditLogs = audit.data.data || [];
        if (!this.transition.from_billing_entity_id) {
          const active = this.entities.find(item => item.status === 'active');
          const target = this.entities.find(item => item.status === 'draft' || item.status === 'ready');
          this.transition.from_billing_entity_id = active ? active.id : null;
          this.transition.to_billing_entity_id = target ? target.id : null;
        }
      } catch (error) { this.showError(error); }
    },
    newEntity() { this.selected = {}; this.form = emptyForm(); },
    editEntity(entity) { this.selected = entity; this.form = Object.assign(emptyForm(), entity); },
    async saveEntity() {
      this.saving = true; this.error = '';
      try {
        if (this.selected.id) {
          await this.$axios.put(this.endpoint(this.$backend.BILLING_ENTITIES.UPDATE, { id: this.selected.id }), this.form);
        } else {
          await this.$axios.post(this.$backend.BILLING_ENTITIES.STORE, this.form);
        }
        this.selected = null; await this.load(); this.$toast.success('Billing entity saved.');
      } catch (error) { this.showError(error); } finally { this.saving = false; }
    },
    async recordTest(test) {
      const entityId = this.selected.id;
      const reference = window.prompt(`Evidence/reference for the successful ${test} test:`);
      if (!reference) return;
      try {
        const url = this.endpoint(this.$backend.BILLING_ENTITIES.READINESS_TEST, { id: entityId, test });
        await this.$axios.put(url, { passed: true, reference });
        await this.load(); this.editEntity(this.entities.find(item => item.id === entityId));
      } catch (error) { this.showError(error); }
    },
    async loadBalances(entityId) {
      try {
        const url = this.endpoint(this.$backend.BILLING_ENTITIES.BALANCES, { id: entityId });
        const response = await this.$axios.get(url, { params: { itemsPerPage: 200 } });
        this.balanceEntityId = entityId;
        this.balances = response.data.data || [];
      } catch (error) { this.showError(error); }
    },
    async scheduleTransition() {
      this.saving = true;
      try {
        const payload = Object.assign({}, this.transition, { transition_at: this.moment(this.transition.transition_at).format() });
        await this.$axios.post(this.$backend.BILLING_ENTITIES.SCHEDULE, payload);
        await this.load(); this.$toast.success('Transition scheduled.');
      } catch (error) { this.showError(error); } finally { this.saving = false; }
    },
    async cancelTransition(item) {
      const reason = window.prompt('Reason for cancelling this transition:');
      if (!reason) return;
      try {
        const url = this.endpoint(this.$backend.BILLING_ENTITIES.CANCEL, { id: item.id });
        await this.$axios.post(url, { reason }); await this.load();
      } catch (error) { this.showError(error); }
    },
    showError(error) { this.error = error.response && error.response.data ? (error.response.data.message || JSON.stringify(error.response.data.errors || error.response.data)) : error.message; },
    formatDate(value) { return value ? this.moment(value).format('YYYY-MM-DD HH:mm:ss') : ''; },
    entityCode(id) { const entity = this.entities.find(item => item.id === id); return entity ? entity.code : `#${id}`; },
    statusColor(status) { return ({ active: 'success', ready: 'info', scheduled: 'warning', archived: 'secondary', cancelled: 'secondary', executed: 'success' })[status] || 'light'; },
    readinessLabel(value) { return value.replace('missing_secret:', 'Missing secret: ').replace('test_not_passed:', 'Test not passed: ').replace('missing:', 'Missing: ').replace(/_/g, ' '); },
  },
};
</script>
